import { invalidationKeysFor, parseRealtimeEvent, queryKeys, STREAM_PATH, type QueryKey, type RealtimeEvent } from "@l2a/shared";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { useAuth } from "@/app/auth/session";
import { useLatest } from "./hooks";

/**
 * Realtime over Server-Sent Events (GET /api/v1/stream, see @l2a/shared realtime.ts).
 *
 * - `invalidate` → invalidates the query-key prefixes from `invalidationKeysFor()`
 *   (e.g. ["client"], ["activity","client",id], ["search"], ["audit"]) — batched per
 *   120 ms so a bulk import does not trigger hundreds of refetches.
 * - `notification` → toast + unread badge update.
 * - `resync` → invalidates everything.
 * Reconnects with capped exponential backoff + jitter when the server closes the
 * stream (the browser handles transient drops itself and resumes with Last-Event-ID).
 *
 * Features can react to raw events with `useRealtimeEvent(handler)`.
 */

export type RealtimeStatus = "idle" | "connecting" | "open" | "reconnecting";

type Listener = (event: RealtimeEvent) => void;
const listeners = new Set<Listener>();

export function subscribeRealtime(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Run `handler` for every realtime event while the component is mounted. */
export function useRealtimeEvent(handler: Listener): void {
  const ref = useLatest(handler);
  useEffect(() => subscribeRealtime((e) => ref.current(e)), [ref]);
}

const StatusContext = createContext<RealtimeStatus>("idle");
export function useRealtimeStatus(): RealtimeStatus {
  return useContext(StatusContext);
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const navigateRef = useLatest(navigate);
  const [status, setStatus] = useState<RealtimeStatus>("idle");
  const userId = me?.user.id;
  const orgId = me?.org.id;

  useEffect(() => {
    if (!userId || !orgId || typeof EventSource === "undefined") return;
    let es: EventSource | null = null;
    let attempt = 0;
    let closed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let flushTimer: ReturnType<typeof setTimeout> | undefined;
    const pending = new Map<string, QueryKey>();

    const flush = () => {
      for (const key of pending.values()) void qc.invalidateQueries({ queryKey: key as unknown[], refetchType: "active" });
      pending.clear();
    };
    const queue = (keys: QueryKey[]) => {
      for (const k of keys) pending.set(JSON.stringify(k), k);
      clearTimeout(flushTimer);
      flushTimer = setTimeout(flush, 120);
    };

    const handle = (event: RealtimeEvent) => {
      switch (event.type) {
        case "invalidate":
          queue(invalidationKeysFor(event));
          break;
        case "notification": {
          if (event.unreadCount !== undefined) qc.setQueryData(queryKeys.notificationsUnreadCount(), { count: event.unreadCount });
          queue(invalidationKeysFor(event));
          const n = event.notification;
          toast(n.title, {
            description: n.body ?? undefined,
            action: n.url ? { label: "Open", onClick: () => navigateRef.current(n.url!) } : undefined,
          });
          break;
        }
        case "resync":
          void qc.invalidateQueries();
          break;
        case "ready":
          break;
      }
      for (const l of listeners) {
        try {
          l(event);
        } catch (err) {
          console.error("realtime listener failed", err);
        }
      }
    };

    const connect = () => {
      if (closed) return;
      setStatus(attempt === 0 ? "connecting" : "reconnecting");
      es = new EventSource(STREAM_PATH, { withCredentials: true });
      es.onopen = () => {
        attempt = 0;
        setStatus("open");
      };
      es.onmessage = (msg: MessageEvent<string>) => {
        const event = parseRealtimeEvent(msg.data);
        if (event) handle(event);
      };
      es.onerror = () => {
        if (!es || es.readyState !== EventSource.CLOSED) {
          setStatus("reconnecting");
          return;
        }
        es.close();
        attempt += 1;
        setStatus("reconnecting");
        // The stream was refused (e.g. 401 after the session expired): re-check the session.
        if (attempt >= 2) void qc.invalidateQueries({ queryKey: queryKeys.me() });
        const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5)) * (0.75 + Math.random() * 0.5);
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    const onOnline = () => {
      if (es && es.readyState === EventSource.CLOSED) {
        clearTimeout(reconnectTimer);
        attempt = 0;
        connect();
      }
    };

    connect();
    window.addEventListener("online", onOnline);
    return () => {
      closed = true;
      es?.close();
      clearTimeout(reconnectTimer);
      clearTimeout(flushTimer);
      window.removeEventListener("online", onOnline);
      setStatus("idle");
    };
  }, [userId, orgId, qc, navigateRef]);

  return <StatusContext.Provider value={status}>{children}</StatusContext.Provider>;
}
